import React, {useCallback, useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import Card from '../Card/Card.jsx';
import CardTitle from '../Headings/CardTitle/CardTitle.jsx';
import Text from '../Text/Text.jsx';
import Button from '../Button/Button.jsx';
import FlexContainer from '../FlexContainer/FlexContainer.jsx';
import ChevronLeft from '../Icons/ChevronLeft.jsx';
import ChevronRight from '../Icons/ChevronRight.jsx';
import css from './ScrollableCardSection.module.scss';

const VISIBLE_WIDTH_STEP = 0.8;

const END_TOLERANCE = 2;

const ScrollableCardSection = ({title, description, label, gap = 16, children}) => {
  const scrollerRef = useRef(null);
  const [canScrollBack, setCanScrollBack] = useState(false);
  const [canScrollForward, setCanScrollForward] = useState(false);

  const syncArrows = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) {
      return;
    }

    const maxScrollLeft = scroller.scrollWidth - scroller.clientWidth;
    setCanScrollBack(scroller.scrollLeft > END_TOLERANCE);
    setCanScrollForward(scroller.scrollLeft < maxScrollLeft - END_TOLERANCE);
  }, []);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) {
      return undefined;
    }

    syncArrows();

    const resizeObserver = new ResizeObserver(syncArrows);
    resizeObserver.observe(scroller);

    return () => {
      resizeObserver.disconnect();
    };
  }, [syncArrows]);

  useEffect(() => {
    syncArrows();
  }, [syncArrows, children]);

  const scrollBy = (direction) => {
    const scroller = scrollerRef.current;
    if (!scroller) {
      return;
    }

    scroller.scrollBy({
      left: direction * scroller.clientWidth * VISIBLE_WIDTH_STEP,
      behavior: 'smooth',
    });
  };

  const handleScrollBack = () => {
    scrollBy(-1);
  };

  const handleScrollForward = () => {
    scrollBy(1);
  };

  const hasArrows = canScrollBack || canScrollForward;

  return (
    <Card className={css.section}>
      {(title || hasArrows) &&
        <FlexContainer justifyContent="space-between" alignItems="center" gap={12}>
          {title ? <CardTitle truncate>{title}</CardTitle> : <span />}
          {hasArrows &&
            <FlexContainer gap={4} alignItems="center" className={css.arrows}>
              <Button
                variant="ghost"
                size="small"
                onClick={handleScrollBack}
                disabled={!canScrollBack}
                aria-label={label ? `Scroll ${label} left` : 'Scroll left'}
              >
                <ChevronLeft />
              </Button>
              <Button
                variant="ghost"
                size="small"
                onClick={handleScrollForward}
                disabled={!canScrollForward}
                aria-label={label ? `Scroll ${label} right` : 'Scroll right'}
              >
                <ChevronRight />
              </Button>
            </FlexContainer>
          }
        </FlexContainer>
      }
      {description &&
        <Text as="p" size="small" color="muted" className={css.description}>{description}</Text>
      }
      <div
        ref={scrollerRef}
        className={css.scroller}
        style={{gap: `${gap}px`}}
        onScroll={syncArrows}
        tabIndex={0}
        role="group"
        aria-label={label ?? title}
      >
        {children}
      </div>
    </Card>
  );
};

ScrollableCardSection.propTypes = {
  title: PropTypes.node,
  description: PropTypes.node,
  label: PropTypes.string,
  gap: PropTypes.number,
  children: PropTypes.node,
};

export default ScrollableCardSection;
